/**
 * Get the ID from the other half of a match
 *
 * Relies on matchId being a concatenation of two user IDs
 *
 * @param  {String} matchId - ID of the match
 * @param  {String} ownId - ID of the current user
 * @return {String} ID of the other user
 */

import memoize from 'fast-memoize';
import mergeWith from 'lodash/mergeWith';

import { emptyArray, emptyObject } from 'Lib/constants/placeholder';
import isNil from 'Lib/utils/isNil';
import { findLast, groupBy } from 'Lib/utils/lodashUtils';

import type { Match as MatchEntitiy, Message, UserProfile } from 'Store/entities/types';
import {
  NOTIFICATION_MATCH_TYPE_BOOST,
  NOTIFICATION_MATCH_TYPE_FAST_MATCH,
  NOTIFICATION_MATCH_TYPE_NORMAL,
  NOTIFICATION_MATCH_TYPE_SUPERBOOST,
  type MatchType,
} from 'Store/updates/constants';

import { MATCH_FILTER_WITH_MESSAGES, MATCH_FILTER_WITHOUT_MESSAGES } from './constants';
import type { DenormalizedMatch, Match } from './types';

export const getMatchId = (match: DenormalizedMatch) => match._id;
export const getOtherIdFromMatch = (matchId: string, ownId: string) => matchId?.replace(ownId, '');

export type GroupedMatchesEntities = {
  [k in
    | typeof MATCH_FILTER_WITH_MESSAGES
    | typeof MATCH_FILTER_WITHOUT_MESSAGES]: DenormalizedMatch[];
};

export type UserMatchInfo = {
  person: UserProfile;
  lastMessage: Message | null;
  matchType: MatchType;
};

// @TODO, very bad, shouldn't mix DenormalizedMatch (regular match) and MatchEntitiy (optimistic)
export const shouldShowIndicator = (match?: DenormalizedMatch | MatchEntitiy | null) => {
  if (!match) {
    return false;
  }

  const { seen, messages = emptyArray, person } = match;
  const matchSeen = seen?.match_seen === true;
  const lastSeenMessageId = seen?.last_seen_msg_id;

  const lastMessage = messages[messages?.length - 1] ?? emptyObject;
  const { _id: lastMessageId, from: lastMessageFrom } = lastMessage;

  const hasMessage = !!lastMessageId;
  const otherUserId = person && typeof person === 'object' ? (person as any)?._id : person;
  const isOwnMessage = lastMessageId && lastMessageFrom && lastMessageFrom !== otherUserId;

  // Need to account for the case where the match has not been opened,
  // but the current user sent a message from the Its A Match Screen
  if (!matchSeen && !isOwnMessage) {
    return true;
  }

  // if user hasn't received any messages from this chat
  if (!hasMessage) {
    return false;
  }

  // if they haven't sent a message (or it hasn't updated)
  // or if last received message is same as last seen message
  return !isOwnMessage && lastSeenMessageId !== lastMessageId;
};

export const getGroupedMatches = memoize(
  (
    matches: DenormalizedMatch[]
  ): Record<
    typeof MATCH_FILTER_WITH_MESSAGES | typeof MATCH_FILTER_WITHOUT_MESSAGES,
    DenormalizedMatch[]
  > =>
    groupBy(matches, (match) => {
      const messageCount: number = match.messages?.length ?? 0;
      return messageCount > 0 ? MATCH_FILTER_WITH_MESSAGES : MATCH_FILTER_WITHOUT_MESSAGES;
    })
);

// Match expiration: get matches with(out) archived matches
// A match is "archived" when it expires and x days have passed
export const getMatchesWithoutArchived = memoize((matches: DenormalizedMatch[]) =>
  matches.filter((match) => !match.is_archived_v2)
);

export const getTimestamp = (match: DenormalizedMatch) => {
  const { messages, timestamp } = match;
  if (!messages?.length) {
    return timestamp;
  }

  const lastMsg = messages[messages.length - 1];
  const lastMessageTimestamp = lastMsg.timestamp;

  return lastMessageTimestamp || Date.parse(lastMsg.sent_date);
};

// Sorting
export const sortAllMatchesByLatestAction = memoize((matches: DenormalizedMatch[]) =>
  matches.sort((a, b) => getTimestamp(b) - getTimestamp(a))
);
export const sortArchivedMatchesByArchiveDatetime = memoize((matches: DenormalizedMatch[]) =>
  matches.filter((match) => match.archive_at).sort((a, b) => b.archive_at! - a.archive_at!)
);

export const sortAllMatchesByPrioritySort = memoize((matches: DenormalizedMatch[]) =>
  matches.sort((a, b) => {
    const priorityATimestamp = Date.parse(a.priority_sort);
    const priorityBTimestamp = Date.parse(b.priority_sort);
    // Only priority sort if the message is unread
    const shouldPrioritySortA = !!priorityATimestamp;
    const shouldPrioritySortB = !!priorityBTimestamp;
    if (shouldPrioritySortA && shouldPrioritySortB) {
      // if both matches are from platinum users, sort by priority_sort timestamp
      return priorityBTimestamp - priorityATimestamp;
    } else if (shouldPrioritySortA && !shouldPrioritySortB) {
      // if match A is a platinum user, that message ranks higher
      return -1;
    } else if (!shouldPrioritySortA && shouldPrioritySortB) {
      // if match B is a platinum user, that message ranks higher
      return 1;
    }
    return getTimestamp(b) - getTimestamp(a);
  })
);

export const sortAllMatchesByCreatedDate = memoize((matches: DenormalizedMatch[]) =>
  matches.sort(
    (a: DenormalizedMatch, b: DenormalizedMatch) =>
      Date.parse(b.created_date) - Date.parse(a.created_date)
  )
);

export const getMatchType = (match: DenormalizedMatch): MatchType => {
  if (!match) {
    return NOTIFICATION_MATCH_TYPE_NORMAL;
  }
  if (match.is_super_boost_match) {
    return NOTIFICATION_MATCH_TYPE_SUPERBOOST;
  }
  if (match.is_boost_match) {
    return NOTIFICATION_MATCH_TYPE_BOOST;
  }
  if (match.is_fast_match) {
    return NOTIFICATION_MATCH_TYPE_FAST_MATCH;
  }
  return NOTIFICATION_MATCH_TYPE_NORMAL;
};

export const getUsersLastMessage = memoize((messages: Message[], userId: string) => {
  if (!messages?.length) {
    return null;
  }
  return findLast(messages, (message) => message.from === userId);
});

// load old messages
const messageLoadMore = (a, b) => Array.from(new Set((b ?? emptyArray).concat(a ?? emptyArray)));

// send new message
const newMessage = (a, b) => Array.from(new Set((a ?? emptyArray).concat(b ?? emptyArray)));

export const matchMergeStrategy = (previousEntity: Match, updatedEntity: Match) => {
  if (!previousEntity) {
    return updatedEntity;
  }
  return mergeWith(previousEntity, updatedEntity, (a, b, key) => {
    // append-unique
    if (key === 'messages') {
      const isNew = updatedEntity.is_new_message;
      return isNew ? newMessage(a, b) : messageLoadMore(a, b);
    }

    // union
    if (key === 'participants') {
      return Array.from(new Set((a ?? emptyArray).concat(b ?? emptyArray)));
    }

    if (key === 'seen') {
      return {
        ...a,
        ...b,
      };
    }
    return isNil(b) && !isNil(a) ? a : b;
  });
};
